import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Header } from '../../Components/Header/Header';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import { decode } from '../../Services/DecodeToken';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  VendingAlert,
  VendingAlertListResponse,
  VendingAlertSeverity,
  VendingMappedCompany,
} from '../../Types/vending';

type StatusFilter = 'open' | 'acknowledged' | 'resolved' | 'all';
type SeverityFilter = '' | VendingAlertSeverity;

const formatAlertDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const severityMeta = (severity: VendingAlertSeverity) => {
  switch (severity) {
    case 'critical':
      return { label: 'Critical', color: COLORS.red, bg: `${COLORS.red}12` };
    case 'high':
      return { label: 'High', color: COLORS.orange, bg: `${COLORS.orange}18` };
    case 'warning':
      return { label: 'Warning', color: COLORS.orange, bg: `${COLORS.orange}18` };
    default:
      return { label: 'Info', color: COLORS.teal, bg: `${COLORS.teal}14` };
  }
};

const statusMeta = (status: string) => {
  const value = (status || '').toLowerCase();
  if (value === 'open') return { color: COLORS.teal, bg: `${COLORS.teal}14` };
  if (value === 'acknowledged') return { color: COLORS.orange, bg: `${COLORS.orange}18` };
  if (value === 'resolved') return { color: COLORS.success, bg: `${COLORS.success}18` };
  return { color: COLORS.lightGray, bg: `${COLORS.lightGray}18` };
};

const machineLabel = (alert: VendingAlert) => {
  if (!alert.machine) return '—';
  return alert.machine.machine_name || `VM-${alert.machine.provider_machine_id}`;
};

const customerLabel = (alert: VendingAlert) => {
  if (!alert.machine) return 'Unassigned';
  if (alert.machine.customer_name) return alert.machine.customer_name;
  if (alert.machine.customer_label) return alert.machine.customer_label;
  return 'Unassigned';
};

const machinePath = (alert: VendingAlert) => {
  if (!alert.machine_id) return null;
  if (alert.alert_type === 'PRODUCT_EMPTY' || alert.alert_type === 'PRODUCT_LOW') {
    return `/vending/machines/${alert.machine_id}/inventory`;
  }
  return `/vending/machines/${alert.machine_id}/alerts`;
};

const headCellSx = {
  py: 1.25,
  px: 2,
  fontWeight: 700,
  fontSize: '0.6875rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: COLORS.lightGray,
  backgroundColor: '#f8f9fa',
  borderBottom: `1px solid ${COLORS.lightGray}35`,
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  py: 1.75,
  px: 2,
  borderBottom: `1px solid ${COLORS.lightGray}30`,
  verticalAlign: 'middle' as const,
};

export const VendingAlertsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMachineId = searchParams.get('machineId') || '';
  const token: any = decode();
  const isAbatixAdmin = Number(token?.roles?.level) >= 100;

  const [alerts, setAlerts] = useState<VendingAlert[]>([]);
  const [companies, setCompanies] = useState<VendingMappedCompany[]>([]);
  const [machines, setMachines] = useState<Array<{ id: number; machine_name: string }>>([]);
  const [stats, setStats] = useState({
    open: 0, critical: 0, high: 0, warning: 0, info: 0, acknowledged: 0, resolved: 0,
  });
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [machineFilter, setMachineFilter] = useState(initialMachineId);
  const [searchInput, setSearchInput] = useState('');
  const [actingId, setActingId] = useState<number | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesAction, setNotesAction] = useState<'acknowledge' | 'resolve'>('acknowledge');
  const [notesAlert, setNotesAlert] = useState<VendingAlert | null>(null);
  const [notes, setNotes] = useState('');
  const skipCompanyReset = useRef(true);

  useEffect(() => {
    if (skipCompanyReset.current) {
      skipCompanyReset.current = false;
      return;
    }
    setMachineFilter('');
  }, [companyFilter]);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200', status: statusFilter });
      if (severityFilter) params.set('severity', severityFilter);
      if (isAbatixAdmin && companyFilter) params.set('cid', companyFilter);
      if (machineFilter) params.set('machine_id', machineFilter);

      const response = await vendingApiClient(`/alerts?${params.toString()}`);
      const json: VendingAlertListResponse = await response.json();
      setAlerts(json.data);
      setCompanies(json.meta?.companies ?? []);
      setMachines(json.meta?.machines ?? []);
      setAsOf(json.meta?.as_of ?? null);
      setStats({
        open: json.meta?.stats?.open ?? 0,
        critical: json.meta?.stats?.critical ?? 0,
        high: json.meta?.stats?.high ?? 0,
        warning: json.meta?.stats?.warning ?? 0,
        info: json.meta?.stats?.info ?? 0,
        acknowledged: json.meta?.stats?.acknowledged ?? 0,
        resolved: json.meta?.stats?.resolved ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, companyFilter, machineFilter, isAbatixAdmin]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const openNotesDialog = (alert: VendingAlert, action: 'acknowledge' | 'resolve') => {
    setNotesAlert(alert);
    setNotesAction(action);
    setNotes(alert.notes || '');
    setNotesOpen(true);
  };

  const submitNotesAction = async () => {
    if (!notesAlert) return;
    setActingId(notesAlert.id);
    setActionMessage(null);
    try {
      await vendingApiClient(`/alerts/${notesAlert.id}/${notesAction}`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes.trim() || undefined }),
      });
      setNotesOpen(false);
      setNotesAlert(null);
      setNotes('');
      setActionMessage({
        text: notesAction === 'acknowledge' ? 'Alert acknowledged.' : 'Alert resolved.',
        severity: 'success',
      });
      await loadAlerts();
    } catch (err) {
      setActionMessage({
        text: err instanceof Error ? err.message : `Failed to ${notesAction} alert`,
        severity: 'error',
      });
    } finally {
      setActingId(null);
    }
  };

  const clearFilters = () => {
    setStatusFilter('open');
    setSeverityFilter('');
    setCompanyFilter('');
    setMachineFilter('');
    setSearchInput('');
  };

  const hasActiveFilters = Boolean(
    statusFilter !== 'open'
    || severityFilter
    || (isAbatixAdmin && companyFilter)
    || machineFilter
    || searchInput.trim(),
  );

  const filteredAlerts = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return alerts;
    return alerts.filter((a) => {
      const hay = [
        a.title,
        a.description,
        a.alert_type_label,
        machineLabel(a),
        customerLabel(a),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [alerts, searchInput]);

  const subtitle = useMemo(() => {
    const parts = [`${stats.open} open`];
    if (stats.critical) parts.push(`${stats.critical} critical`);
    if (asOf) parts.push(`As of ${formatUTC(asOf)} UTC`);
    return parts.join(' · ');
  }, [stats, asOf]);

  return (
    <PageContainer>
      <Header title="Alerts" subtitle={subtitle} count={filteredAlerts.length}>
        <FreshnessBadge asOf={asOf} />
        <Button variant="outlined" onClick={loadAlerts} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: 2,
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Alert, machine, customer…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', md: '0 1 260px' } }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              label="Severity"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </Select>
          </FormControl>

          {isAbatixAdmin && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                label="Customer"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <MenuItem value="">All customers</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.cid} value={String(c.cid)}>{c.customer_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Machine</InputLabel>
            <Select
              label="Machine"
              value={machineFilter}
              onChange={(e) => setMachineFilter(e.target.value)}
            >
              <MenuItem value="">All machines</MenuItem>
              {machines.map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>{m.machine_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Button variant="text" onClick={clearFilters}>Clear Filters</Button>
          )}
        </Box>

        {actionMessage && (
          <Box sx={{ px: { xs: 2, md: 3 }, flexShrink: 0 }}>
            <Alert
              severity={actionMessage.severity}
              sx={{ mb: 2 }}
              onClose={() => setActionMessage(null)}
            >
              {actionMessage.text}
            </Alert>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: { xs: 2, md: 3 } }}>
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={loadAlerts}>Retry</Button>}
            >
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && filteredAlerts.length === 0 && (
          <Box
            sx={{
              mx: { xs: 2, md: 3 },
              textAlign: 'center',
              py: 8,
              px: 2,
              borderRadius: 2,
              border: `1px dashed ${COLORS.lightGray}`,
              backgroundColor: '#fafbfc',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
              No alerts
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
              {statusFilter === 'open' && !hasActiveFilters
                ? 'Nothing needs attention right now.'
                : 'No alerts match the current filters.'}
            </Typography>
          </Box>
        )}

        {!loading && !error && filteredAlerts.length > 0 && (
          <Box sx={{ flex: 1, minHeight: 0, px: { xs: 2, md: 3 }, pb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${COLORS.lightGray}40`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader size="medium" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headCellSx, width: 110 }}>Severity</TableCell>
                      <TableCell sx={headCellSx}>Alert</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 180 }}>Machine</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 160 }}>Customer</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 160 }}>Date</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 120 }}>Status</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 200, textAlign: 'right' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAlerts.map((alert) => {
                      const sev = severityMeta(alert.severity);
                      const st = statusMeta(alert.status);
                      const path = machinePath(alert);
                      const busy = actingId === alert.id;
                      return (
                        <TableRow
                          key={alert.id}
                          hover
                          sx={{
                            cursor: path ? 'pointer' : 'default',
                            '&:hover': { backgroundColor: '#fafbfc' },
                          }}
                          onClick={() => {
                            if (path) navigate(path);
                          }}
                        >
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              size="small"
                              label={sev.label}
                              sx={{
                                height: 24,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                bgcolor: sev.bg,
                                color: sev.color,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.darkGray }}>
                              {alert.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                              {alert.alert_type_label}
                              {alert.description ? ` · ${alert.description}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: path ? COLORS.teal : COLORS.darkGray }}
                            >
                              {machineLabel(alert)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography variant="body2">{customerLabel(alert)}</Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {formatAlertDate(alert.last_detected)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              size="small"
                              label={alert.status}
                              sx={{
                                height: 22,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize',
                                bgcolor: st.bg,
                                color: st.color,
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{ ...bodyCellSx, textAlign: 'right' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box sx={{ display: 'inline-flex', gap: 1, justifyContent: 'flex-end' }}>
                              {alert.status === 'open' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={busy}
                                  onClick={() => openNotesDialog(alert, 'acknowledge')}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: COLORS.teal,
                                    color: COLORS.teal,
                                    minWidth: 0,
                                  }}
                                >
                                  Ack
                                </Button>
                              )}
                              {(alert.status === 'open' || alert.status === 'acknowledged') && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={busy}
                                  onClick={() => openNotesDialog(alert, 'resolve')}
                                  sx={{ textTransform: 'none', bgcolor: COLORS.teal, minWidth: 0 }}
                                >
                                  Resolve
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}
      </Box>

      <Dialog open={notesOpen} onClose={() => actingId == null && setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {notesAction === 'acknowledge' ? 'Acknowledge alert' : 'Resolve alert'}
        </DialogTitle>
        <DialogContent>
          {notesAlert && (
            <Typography variant="body2" sx={{ mb: 2, color: COLORS.lightGray }}>
              {notesAlert.title} · {machineLabel(notesAlert)}
            </Typography>
          )}
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNotesOpen(false)} disabled={actingId != null}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitNotesAction}
            disabled={actingId != null}
            sx={{ bgcolor: COLORS.teal }}
          >
            {actingId != null
              ? 'Saving…'
              : notesAction === 'acknowledge' ? 'Acknowledge' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default VendingAlertsPage;
